/**
 * The static drawing of a flow map: one SVG that the server renders, the RSC
 * template preview shows, and the live component displays until its canvas
 * chunk has loaded (so the first paint and the canvas agree on every box).
 *
 * Geometry comes from layout.ts (the same boxes and handles the canvas and the
 * auto layout use). Cards are the shared FlowNodeCard inside foreignObject, so
 * text wraps and clamps like HTML. The drawing is aria-hidden: the table view
 * next to it carries the same content for screen readers and crawlers.
 *
 * Paint order: group frames at the back, then arrows, then cards, then the
 * arrow labels on top so a label is never covered by a card it runs past.
 *
 * No handlers and no "use client". It must sit inside a positioned box (the
 * `.wa-fm__stage`): it centres itself there and never scales a small map above
 * its natural size, like the canvas's fit-view with maxZoom 1.
 */
import type { ReactElement } from "react";
import { FlowNodeCard } from "./FlowNodeCard.js";
import { KindIcon } from "./icons.js";
import { edgeHandles, edgePath, flowBounds, handlePoint, nodeBoxes } from "./layout.js";
import { round1 } from "./layout/geometry.js";
import { readText, type FlowEdgeKind, type FlowMapDoc, type FlowMapLabels, type FlowNodeMark } from "./model.js";

export interface FlowMapPictureProps {
  doc: FlowMapDoc;
  locale?: string;
  labels: FlowMapLabels;
  /** Makes the arrow marker ids unique on the page (e.g. the block id). Sanitised to [A-Za-z0-9_-]. */
  idPrefix: string;
  /** The host's marks by node id (FlowMapProps.nodeMarks). */
  marks?: Readonly<Record<string, FlowNodeMark>>;
  className?: string;
}

/** `xmlns` for the HTML root inside a foreignObject; spread because React's div props do not declare it. */
const XHTML = { xmlns: "http://www.w3.org/1999/xhtml" };

/** Space around the map inside the drawing. */
const PAD = 48;
/** The box an arrow label is centred in; the pill inside shrinks to its text. */
const LABEL_W = 200;
const LABEL_H = 26;
/** Height of a group's title band. */
const GROUP_HEAD = 44;
/** Presentation-attribute colour used only if FM_CSS is missing, so arrows never vanish. */
const FALLBACK_INK = "#94a3b8";

const MARKERS = ["arrow", "yes", "no"] as const;
type MarkerName = (typeof MARKERS)[number];

const markerOf = (kind: FlowEdgeKind | undefined): MarkerName => (kind === "yes" ? "yes" : kind === "no" ? "no" : "arrow");

/** An id prefix that is safe inside `url(#...)` and an id attribute. */
export function sanitizeIdPrefix(raw: string): string {
  return raw.replace(/[^A-Za-z0-9_-]/g, "") || "wa-fm";
}

export function FlowMapPicture({ doc, locale, labels, idPrefix, marks, className }: FlowMapPictureProps): ReactElement {
  if (!doc.nodes.length) return <div className="wa-fm__empty">{labels.emptyReadOnly}</div>;

  const prefix = sanitizeIdPrefix(idPrefix);
  const vb = flowBounds(doc, PAD);
  const boxes = nodeBoxes(doc);
  const arrows = doc.edges.flatMap((edge) => {
    const from = boxes.get(edge.source);
    const to = boxes.get(edge.target);
    if (!from || !to) return [];
    const h = edgeHandles(doc, edge);
    const path = edgePath(handlePoint(from, h.source), h.source, handlePoint(to, h.target), h.target, doc.edgeStyle);
    return [{ edge, path, kind: edge.kind ?? "default", label: readText(edge.label, locale).trim() }];
  });
  const groups = doc.nodes.filter((n) => n.kind === "group");
  const cards = doc.nodes.filter((n) => n.kind !== "group");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className ? `wa-fm__picture ${className}` : "wa-fm__picture"}
      viewBox={`${vb.x} ${vb.y} ${vb.width} ${vb.height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      style={{ maxWidth: `${vb.width}px`, maxHeight: `${vb.height}px` }}
    >
      <defs>
        {MARKERS.map((m) => (
          <marker
            key={m}
            id={`${prefix}-${m}`}
            viewBox="-10 -10 20 20"
            markerWidth={20}
            markerHeight={20}
            markerUnits="userSpaceOnUse"
            orient="auto-start-reverse"
            refX={0}
            refY={0}
          >
            <polyline
              className={`wa-fm-pic__marker wa-fm-pic__marker--${m}`}
              points="-5,-4 0,0 -5,4 -5,-4"
              fill={FALLBACK_INK}
              stroke={FALLBACK_INK}
              strokeWidth={1}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        ))}
      </defs>

      <g className="wa-fm-pic__groups">
        {groups.map((g) => {
          const b = boxes.get(g.id)!;
          const title = readText(g.title, locale);
          const hasTitle = title.trim().length > 0;
          return (
            <g key={g.id}>
              <rect
                className="wa-fm-pic__group-box"
                x={b.x}
                y={b.y}
                width={b.width}
                height={b.height}
                rx={16}
                ry={16}
                fill="none"
                stroke={FALLBACK_INK}
                strokeWidth={1.5}
                strokeDasharray="6 5"
              />
              <foreignObject x={b.x} y={b.y} width={b.width} height={Math.min(GROUP_HEAD, b.height)}>
                <div {...XHTML} className="wa-fm-pic__group-head">
                  <span className="wa-fm-node__tab">
                    <KindIcon kind="group" size={12} />
                    <span className={hasTitle ? "wa-fm-node__title" : "wa-fm-node__title is-empty"}>{hasTitle ? title : labels.kindGroup}</span>
                  </span>
                </div>
              </foreignObject>
            </g>
          );
        })}
      </g>

      <g className="wa-fm-pic__edges">
        {arrows.map(({ edge, path, kind }) => (
          <path
            key={edge.id}
            className={`wa-fm-pic__edge wa-fm-pic__edge--${kind}`}
            d={path.d}
            fill="none"
            stroke={FALLBACK_INK}
            strokeWidth={1.5}
            strokeDasharray={kind === "optional" ? "6 5" : undefined}
            markerEnd={`url(#${prefix}-${markerOf(edge.kind)})`}
          />
        ))}
      </g>

      <g className="wa-fm-pic__nodes">
        {cards.map((n) => {
          const b = boxes.get(n.id)!;
          return (
            <foreignObject key={n.id} x={b.x} y={b.y} width={b.width} height={b.height}>
              <div {...XHTML} className="wa-fm-pic__node">
                <FlowNodeCard node={n} locale={locale} labels={labels} mode="picture" mark={marks?.[n.id]} />
              </div>
            </foreignObject>
          );
        })}
      </g>

      <g className="wa-fm-pic__labels">
        {arrows.map(({ edge, path, kind, label }) =>
          label ? (
            <foreignObject key={edge.id} x={round1(path.labelX - LABEL_W / 2)} y={round1(path.labelY - LABEL_H / 2)} width={LABEL_W} height={LABEL_H}>
              <div {...XHTML} className="wa-fm-pic__label-box">
                <span className={`wa-fm-pic__label wa-fm-pic__label--${kind}`}>{label}</span>
              </div>
            </foreignObject>
          ) : null,
        )}
      </g>
    </svg>
  );
}
